'use client';
import 'client-only';
import { useSiteStore } from '@/store/SiteStoreProvider';
import { useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';

/**
 * Hook that stores experiment values received from server components into the client-side store.
 * This allows server-side experiment data to be easily accessed by client components throughout the app.
 *
 * @param experimentsFromServer - A record of experiment names mapped to their variants.
 *                               Each variant can be 'experiment', 'control', or null.
 * @returns An object containing the current experiment variants from the store.
 */
export const useStoreExperimentValuesFromServer = (
  experimentsFromServer: Record<string, 'experiment' | 'control' | null>,
) => {
  const { setExperimentVariant, experimentVariants } = useSiteStore(
    useShallow((state) => ({
      setExperimentVariant: state.setExperimentVariant,
      experimentVariants: state.experimentVariants,
    })),
  );

  useEffect(() => {
    if (experimentsFromServer) {
      Object.entries(experimentsFromServer).forEach(([experiment, variant]) => {
        if (variant) {
          setExperimentVariant(experiment, variant);
        }
      });
    }
  }, [experimentsFromServer]);

  return { experimentVariants };
};
